import type { AdrDocument } from '@backstage-community/plugin-adr-common';

import adrPlugin from '@backstage-community/plugin-adr/alpha';
import { Select } from '@backstage/core-components';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { useSearch } from '@backstage/plugin-search-react';
import {
  SearchFilterBlueprint,
  SearchFilterResultTypeBlueprint,
} from '@backstage/plugin-search-react/alpha';
import { Icon } from '@iconify/react';
import { useEffect } from 'react';

import { adrStatusOptions } from './adrStatus';

const getSelectedAdrStatus = (statusFilter: unknown) =>
  adrStatusOptions.find(({ value }) =>
    (Array.isArray(statusFilter)
      ? statusFilter.includes(value)
      : statusFilter === value),
  )?.value;

const withoutFilters = <T extends Record<string, unknown>>(
  filters: T,
  names: readonly string[],
) => {
  if (names.every(name => !(name in filters))) return filters;

  const nextFilters = { ...filters };
  for (const name of names) delete nextFilters[name];
  return nextFilters;
};

const getCatalogFilterElements = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>(
      [
        '[data-testid="search-selectfilter-next"]',
        '[data-testid="search-checkboxfilter-next"]',
      ].join(','),
    ),
  ).filter(element =>
    ['Kind', 'Lifecycle'].some(label => element.textContent.includes(label)),
  );

const isAdrDocument = (result: unknown): result is AdrDocument =>
  result !== null
  && typeof result === 'object'
  && 'entityRef' in result
  && typeof result.entityRef === 'string'
  && result.entityRef.length > 0;

const AdrStatusSearchFilter = ({ className }: { className: string }) => {
  const { filters, setFilters, types } = useSearch();
  const isAdrSearch = types.includes('adr');
  const selectedStatus = getSelectedAdrStatus(filters.status);

  const setStatusFilter = (selected: number | number[] | string | string[]) => {
    setFilters(prevFilters => {
      const otherFilters = withoutFilters(prevFilters, ['status']);

      if (Array.isArray(selected)) return otherFilters;

      const status = getSelectedAdrStatus(String(selected));
      return status ? { ...otherFilters, status } : otherFilters;
    });
  };

  useEffect(() => {
    setFilters(prevFilters => {
      if (isAdrSearch) {
        return withoutFilters(prevFilters, ['kind', 'lifecycle']);
      }

      return withoutFilters(prevFilters, ['status']);
    });
  }, [isAdrSearch, setFilters]);

  useEffect(() => {
    if (!isAdrSearch) return;

    const catalogFilterElements = getCatalogFilterElements();

    if (!catalogFilterElements.length) return;

    const previousDisplays = catalogFilterElements.map(
      element => element.style.display,
    );
    for (const element of catalogFilterElements) {
      element.style.display = 'none';
    }

    return () => {
      for (const [index, element] of catalogFilterElements.entries()) {
        const display = previousDisplays[index];
        if (display) {
          element.style.display = display;
        }
      }
    };
  }, [isAdrSearch]);

  if (!isAdrSearch) return <div hidden />;

  return (
    <div className={className}>
      <Select
        items={[...adrStatusOptions]}
        label="ADR State"
        onChange={setStatusFilter}
        placeholder="All"
        selected={selectedStatus}
      />
    </div>
  );
};

export const adrModuleIpen = createFrontendModule({
  extensions: [
    SearchFilterResultTypeBlueprint.make({
      name: 'adr',
      params: {
        icon: <Icon icon="material-symbols:assignment-turned-in" />,
        name: 'Architecture Decision Records',
        value: 'adr',
      },
    }),
    SearchFilterBlueprint.make({
      name: 'adr-status',
      params: {
        component: AdrStatusSearchFilter,
      },
    }),
    adrPlugin.getExtension('entity-content:adr').override({ disabled: true }),
    adrPlugin.getExtension('search-result-list-item:adr').override({
      params: {
        component: () =>
          import('./components/AdrSearchResultListItem').then(
            m =>
              ({ result, ...rest }) =>
                (isAdrDocument(result)
                  ? (
                      <m.AdrSearchResultListItem
                        {...rest}
                        icon={(
                          <Icon
                            fontSize={32}
                            icon="material-symbols:assignment-turned-in"
                          />
                        )}
                        result={result}
                      />
                    )
                  : null),
          ),
        predicate: result => result.type === 'adr',
      },
    }),
  ],
  pluginId: 'adr',
});
